import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Package,
  Building2,
  Search,
  ChevronRight,
  X,
} from 'lucide-react';
import { getBrandBySlug } from '../api';
import { BrandBannerItem } from '../components/brand/BrandResponsiveBanner';
import BrandIntegratedHero from '../components/brand/BrandIntegratedHero';
import BrandProductSection from '../components/brand/BrandProductSection';
import { useBrandPageSeo } from '../hooks/useBrandPageSeo';
import { usePricingRefresh } from '../hooks/usePricingRefresh';

interface Product {
  _id: string;
  name: string;
  slug: string;
  price: {
    mrp: number;
    selling: number;
  };
  images: string[];
  rating?: number;
  reviewCount?: number;
  brand?: string | { name: string } | null;
  createdAt?: string;
}

interface Brand {
  _id: string;
  name: string;
  slug: string;
  logo?: string;
  description?: string;
  productCount?: number;
  seo?: { metaTitle?: string; metaDescription?: string; keywords?: string[] };
}

type BrandSection = {
  _id?: string;
  title: string;
  layout: 'slider' | 'grid';
  products: Product[];
};

/* ============================================================
   ANIMATION VARIANTS
   ============================================================ */
const scaleIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.4 } }
};

/* ============================================================
   HELPER FUNCTIONS
   ============================================================ */
const calculateDiscount = (mrp: number, selling: number) => {
  if (!mrp || !selling || mrp <= selling) return 0;
  return Math.round(((mrp - selling) / mrp) * 100);
};

/* ============================================================
   STICKY FILTER BAR
   ============================================================ */
const FilterBar: React.FC<{
  brandName: string;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  productCount: number;
  filteredCount: number;
}> = ({ brandName, searchQuery, setSearchQuery, productCount, filteredCount }) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className="sticky top-0 z-40 bg-gray-50/95 backdrop-blur-md border-b border-gray-200/80">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row gap-3 py-3 items-stretch sm:items-center">
          {/* Search */}
          <div className={`relative flex-1 max-w-md transition-all duration-300 ${isFocused ? 'transform scale-[1.01]' : ''}`}>
            <div className={`absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl blur opacity-0 transition-opacity duration-300 ${isFocused ? 'opacity-20' : ''}`} />
            <div className="relative flex items-center bg-white border border-gray-200 rounded-xl overflow-hidden focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
              <Search className="w-4 h-4 text-gray-400 ml-4 flex-shrink-0" />
              <input
                type="text"
                placeholder={`Search in ${brandName}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                className="flex-1 bg-transparent border-none outline-none px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="mr-2 p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Count */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400 hidden lg:inline">
              {filteredCount} of {productCount}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};


/* ============================================================
   SKELETON COMPONENTS
   ============================================================ */
const SkeletonProduct = () => (
  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
    <div className="aspect-square bg-gray-200" />
    <div className="p-4">
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
      <div className="h-4 bg-gray-200 rounded w-1/2 mb-3" />
      <div className="h-5 bg-gray-200 rounded w-1/3 mb-3" />
      <div className="h-10 bg-gray-200 rounded-xl" />
    </div>
  </div>
);

const LoadingState = () => (
  <div className="min-h-screen bg-gray-50">
    {/* Hero Skeleton */}
    <div className="bg-slate-900 py-12 md:py-16">
      <div className="container mx-auto px-4">
        <div className="animate-pulse">
          <div className="h-4 bg-white/10 rounded-full w-48 mb-6" />
          <div className="flex items-center gap-5">
            <div className="w-24 h-24 rounded-2xl bg-white/10" />
            <div className="flex-1">
              <div className="h-8 bg-white/10 rounded-lg w-48 mb-2" />
              <div className="h-4 bg-white/10 rounded w-72 mb-4" />
              <div className="flex gap-3">
                <div className="w-24 h-10 rounded-xl bg-white/10" />
                <div className="w-24 h-10 rounded-xl bg-white/10" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div className="container mx-auto px-4 py-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
        {Array.from({ length: 10 }).map((_, i) => (
          <SkeletonProduct key={i} />
        ))}
      </div>
    </div>
  </div>
);

/* ============================================================
   EMPTY STATE
   ============================================================ */
const EmptyState: React.FC<{ searchQuery: string; onClear: () => void }> = ({ searchQuery, onClear }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.4 }}
    className="flex flex-col items-center justify-center py-20 text-center"
  >
    <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-slate-100 to-gray-200 flex items-center justify-center mb-6">
      <Search className="w-10 h-10 text-gray-400" />
    </div>
    <h3 className="text-2xl font-bold text-gray-900 mb-2">No products found</h3>
    <p className="text-gray-500 mb-6 max-w-md">
      We couldn't find any products matching &quot;<span className="font-semibold text-gray-700">{searchQuery}</span>&quot;.
    </p>
    <button
      onClick={onClear}
      className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-xl font-medium hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/20"
    >
      <X className="w-4 h-4" />
      Clear Search
    </button>
  </motion.div>
);

/* ============================================================
   ERROR STATE
   ============================================================ */
const ErrorState: React.FC<{ error: string; onRetry: () => void }> = ({ error, onRetry }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center max-w-md"
    >
      <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center mx-auto mb-6 border border-red-100">
        <Building2 className="w-10 h-10 text-red-400" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-3">Brand not found</h2>
      <p className="text-gray-500 mb-8">{error}</p>
      <Link
        to="/brands"
        className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-xl font-medium hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/20"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Brands
      </Link>
    </motion.div>
  </div>
);

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
const BrandProducts: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [brand, setBrand] = useState<Brand | null>(null);
  const [banners, setBanners] = useState<BrandBannerItem[]>([]);
  const [sections, setSections] = useState<BrandSection[]>([]);
  const [productCount, setProductCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const pricingRefresh = usePricingRefresh();

  useEffect(() => {
    if (slug) {
      fetchBrandAndProducts();
    }
  }, [slug, pricingRefresh]);

  const fetchBrandAndProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getBrandBySlug(slug!);
      if (!res.success || !res.data?.brand) {
        setError(res.message || 'Brand not found');
        return;
      }
      setBrand(res.data.brand);
      setBanners(res.data.banners || []);
      setSections(res.data.sections || []);
      setProductCount(res.data.productCount ?? 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load brand products');
    } finally {
      setLoading(false);
    }
  };

  useBrandPageSeo(brand?.name || '', brand?.seo, brand?.slug);

  const sortProducts = (list: Product[]) => {
    const result = [...list];
    // Always show lower price first by default
    result.sort((a, b) => (a.price?.selling || 0) - (b.price?.selling || 0));
    return result;
  };

  const displaySections = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return sections
      .map((section) => {
        let prods = [...section.products];
        if (query) {
          prods = prods.filter((p) => p.name.toLowerCase().includes(query));
        }
        return { ...section, products: sortProducts(prods) };
      })
      .filter((section) => section.products.length > 0);
  }, [sections, searchQuery]);

  const visibleProductCount = useMemo(
    () => displaySections.reduce((n, s) => n + s.products.length, 0),
    [displaySections]
  );

  const catalogProducts = useMemo(
    () => sections.flatMap((s) => s.products),
    [sections]
  );

  const avgDiscount = useMemo(() => {
    if (catalogProducts.length === 0) return 0;
    const discounts = catalogProducts
      .map((p) => calculateDiscount(p.price?.mrp || 0, p.price?.selling || 0))
      .filter((d) => d > 0);
    return discounts.length > 0 ? Math.round(discounts.reduce((a, b) => a + b, 0) / discounts.length) : 0;
  }, [catalogProducts]);

  const avgRating = useMemo(() => {
    if (catalogProducts.length === 0) return '0';
    const ratings = catalogProducts.map((p) => p.rating || 0).filter((r) => r > 0);
    return ratings.length > 0 ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1) : '0';
  }, [catalogProducts]);

  if (loading) return <LoadingState />;
  if (error || !brand) return <ErrorState error={error || 'Brand not found'} onRetry={fetchBrandAndProducts} />;
  return (
    <div className="min-h-screen bg-gray-50">
      <BrandIntegratedHero
        brand={brand}
        banners={banners}
        productCount={productCount}
        avgDiscount={avgDiscount}
        avgRating={avgRating}
      />

      <FilterBar
        brandName={brand.name}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        productCount={productCount}
        filteredCount={visibleProductCount}
      />

      {displaySections.length === 0 ? (
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 bg-white border-t border-gray-100">
          {searchQuery.trim() ? (
            <EmptyState searchQuery={searchQuery} onClear={() => setSearchQuery('')} />
          ) : (
            <div className="text-center py-16 text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-lg font-medium text-gray-700">No product sections yet</p>
              <p className="text-sm mt-1">Check back soon for {brand.name} collections.</p>
            </div>
          )}
        </div>
      ) : (
        displaySections.map((section) => (
          <BrandProductSection
            key={section._id || section.title}
            title={section.title}
            layout={section.layout}
            products={section.products}
          />
        ))
      )}

      {/* Back to Brands */}
      <section className="py-10 md:py-14 border-t border-gray-200/80">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            variants={scaleIn}
            className="text-center"
          >
            <Link
              to="/brands"
              className="inline-flex items-center gap-2 text-slate-900 font-bold hover:text-blue-600 transition-colors text-base md:text-lg"
            >
              <ArrowLeft className="w-5 h-5" />
              View All Brands
              <ChevronRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BrandProducts;
